import { DROPOFF_PLACE_KINDS, type DropoffPlaceKind, type TransportPlaceOption, makeSetDropoffsRequest } from "@seagames/contracts";
import { Modal, ModalActions, Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import { dropoffsOptions, invalidateFleet, placeOptionsOptions, putDropoffs } from "../../lib/api/transport-fleet";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { toast } from "../../lib/ui/toast";
import { useTransportFleetFormMessages as useFleetFormMessages } from "../../lib/i18n/use-form-schemas";

/** `VENUE:<id>` / `PLACE:<id>` — one Select value per drop-off place. */
const placeValue = (p: { kind: string; id: string }) => `${p.kind}:${p.id}`;
const isDropoffKind = (k: string): k is DropoffPlaceKind => (DROPOFF_PLACE_KINDS as readonly string[]).includes(k);

/**
 * Contingent drop-offs (PLAN X5, Q2): where each contingent's arrivals go
 * before M11 gives them rooms — one venue or transport place per
 * organisation, per games. Suggested transfers take it as their destination;
 * a NOC's "Request a trip" puts it first. Saving is ONE replace-set PUT for
 * the games: the rows on screen are the rows kept.
 */
export function DropoffsModal({ gamesId, onClose }: { gamesId: string; onClose: () => void }) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const messages = useFleetFormMessages();
	const orgs = useQuery(responsibleOrganizationOptionsOptions());
	const places = useQuery(placeOptionsOptions(gamesId));
	const stored = useQuery(dropoffsOptions(gamesId));
	const [own, setOwn] = useState<Record<string, string> | null>(null);
	const [busy, setBusy] = useState(false);

	const storedMap = useMemo(() => {
		const m: Record<string, string> = {};
		for (const d of stored.data?.items ?? []) m[d.responsible_organization.id] = placeValue(d.place);
		return m;
	}, [stored.data]);
	const draft = own ?? storedMap;
	const dirty = own !== null && JSON.stringify(Object.entries(own).filter(([, v]) => v).sort()) !== JSON.stringify(Object.entries(storedMap).sort());

	const options = useMemo<SelectOption[]>(() => {
		const list = (places.data ?? []).filter((p): p is TransportPlaceOption & { cluster: NonNullable<TransportPlaceOption["cluster"]> } => isDropoffKind(p.kind) && p.cluster !== null);
		return list.map((p) => ({ value: placeValue(p), label: p.name, description: `${p.cluster.code} · ${p.cluster.name}`, group: t(`placeKind.${p.kind}`) }));
	}, [places.data, t]);

	// Organisations with a drop-off first, then the rest; both by name. The order
	// is fixed when the data arrives: re-sorting while picking would move the row away.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `draft` deliberately not a dependency (see above)
	const rows = useMemo(() => {
		const list = [...(orgs.data ?? [])];
		list.sort((a, b) => Number(!draft[a.responsible_organization_id]) - Number(!draft[b.responsible_organization_id]) || a.name.localeCompare(b.name));
		return list;
	}, [orgs.data, stored.data]);

	const save = async () => {
		const body = {
			games_id: gamesId,
			rows: Object.entries(draft)
				.filter(([, v]) => v)
				.map(([orgId, v]) => {
					const [kind = "", id = ""] = v.split(":");
					return { responsible_organization_id: orgId, place: { kind: kind as DropoffPlaceKind, id } };
				}),
		};
		const parsed = makeSetDropoffsRequest(messages).safeParse(body);
		if (!parsed.success) {
			toast.error(new Error(parsed.error.issues[0]?.message ?? ""), t("dropoffs.saveFailed"));
			return;
		}
		setBusy(true);
		try {
			await putDropoffs(parsed.data);
			await invalidateFleet();
			setOwn(null);
			toast.success(t("dropoffs.saved", { count: parsed.data.rows.length }));
			onClose();
		} catch (err) {
			toast.error(err, t("dropoffs.saveFailed"));
		} finally {
			setBusy(false);
		}
	};

	const loading = orgs.isPending || places.isPending || stored.isPending;
	const failed = orgs.error ?? places.error ?? stored.error;
	const set = (orgId: string, v: string | null) => setOwn({ ...draft, [orgId]: v ?? "" });
	const setCount = Object.values(draft).filter(Boolean).length;

	return (
		<Modal
			open
			onClose={onClose}
			size="lg"
			title={t("dropoffs.title")}
			description={t("dropoffs.description")}
			dismissible={!busy}
			footer={
				<ModalActions start={!loading && !failed ? <span className="text-[0.78125rem] text-gms-ink-3">{t("dropoffs.count", { count: setCount, total: rows.length })}</span> : undefined}>
					<button type="button" className="btn btn-line btn-sm" onClick={onClose} disabled={busy}>
						{tc("action.cancel")}
					</button>
					<button type="button" className="btn btn-primary btn-sm" onClick={() => void save()} disabled={busy || !dirty}>
						{busy && <span className="loading loading-spinner loading-xs" />}
						{t("dropoffs.save")}
					</button>
				</ModalActions>
			}
		>
			{loading ? (
				<div className="skeleton h-[12rem] rounded-[0.625rem]" />
			) : failed ? (
				<p role="alert" className="rounded-field bg-error/8 px-[0.875rem] py-[0.75rem] text-sm">
					{errorMessage(failed)}
				</p>
			) : options.length === 0 ? (
				<p className="rounded-field bg-gms-soft px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-ink-2">{t("dropoffs.noPlaces")}</p>
			) : (
				<ul className="divide-y divide-gms-line rounded-field border border-gms-line">
					{rows.map((o) => (
						<li key={o.responsible_organization_id} className="grid items-center gap-[0.5rem] px-[0.875rem] py-[0.5rem] sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
							<span className="grid min-w-0">
								<b className="truncate text-[0.84375rem] font-semibold text-gms-navy-ink">{o.name}</b>
								{o.organization_type && <span className="truncate text-[0.75rem] text-gms-ink-3">{o.organization_type.name}</span>}
							</span>
							<Select
								value={draft[o.responsible_organization_id] || null}
								onChange={(v) => set(o.responsible_organization_id, v)}
								options={options}
								placeholder={t("dropoffs.none")}
								clearable
								aria-label={t("dropoffs.pickFor", { name: o.name })}
							/>
						</li>
					))}
				</ul>
			)}
		</Modal>
	);
}
